import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { userService } from '../services/api';
import { Cake, Gift, PartyPopper, Sparkles, Calendar, X } from 'lucide-react';

const BirthdayIcon = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: birthdaysData, isLoading } = useQuery({
    queryKey: ['birthdays-week'],
    queryFn: () => userService.getBirthdayUsers(),
    staleTime: 5 * 60 * 1000, // 5 minutos
    refetchInterval: 60 * 60 * 1000, // Refetch a cada hora
  });

  const birthdays = birthdaysData?.data?.data || [];
  const hasBirthdays = birthdays.length > 0;

  const formatDate = (dateString) => {
    // Parse a data ISO (YYYY-MM-DD) corretamente para evitar problemas de fuso horário
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month é 0-indexed no JS
    
    return date.toLocaleDateString('pt-BR', { 
      day: 'numeric', 
      month: 'long',
      weekday: 'long'
    });
  };

  const getDaysUntil = (dateString) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Parse a data ISO (YYYY-MM-DD) corretamente para evitar problemas de fuso horário
    const [year, month, day] = dateString.split('-').map(Number);
    const birthday = new Date(today.getFullYear(), month - 1, day); // month é 0-indexed no JS
    
    // Se o aniversário já passou este ano, considerar o próximo ano
    if (birthday < today) {
      birthday.setFullYear(today.getFullYear() + 1);
    }
    
    const diffTime = birthday - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (!hasBirthdays) {
    return null; // Não mostrar se não houver aniversariantes
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        className="relative p-2 rounded-full hover:bg-secondary-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
        title="Aniversariantes da Semana"
      >
        <Cake className="h-5 w-5 text-pink-500 animate-bounce" />
        <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-2 h-2 rounded-full bg-pink-500 animate-ping"></span>
      </button>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setIsModalOpen(false)}
            ></div>

            {/* Modal Panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 px-6 py-4 border-b-2 border-pink-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Cake className="h-6 w-6 text-pink-500 animate-bounce" />
                      <Sparkles className="h-3 w-3 text-yellow-400 absolute -top-1 -right-1 animate-ping" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">
                      🎉 Aniversariantes da Semana
                    </h3>
                  </div>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="bg-white px-6 py-4 max-h-96 overflow-y-auto">
                {isLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-pulse text-gray-500">Carregando aniversariantes...</div>
                  </div>
                ) : birthdays.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    Nenhum aniversariante esta semana.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {birthdays.map((day, dayIndex) => {
                      const daysUntil = getDaysUntil(day.date);
                      const isToday = daysUntil === 0;
                      
                      return (
                        <div
                          key={dayIndex}
                          className={`rounded-lg p-4 border-2 ${
                            isToday 
                              ? 'border-pink-400 bg-gradient-to-r from-pink-50 to-purple-50' 
                              : 'border-pink-200 bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-5 w-5 text-pink-500" />
                              <span className="font-semibold text-gray-800">
                                {formatDate(day.date)}
                              </span>
                              {isToday && (
                                <span className="ml-2 px-2 py-1 bg-pink-500 text-white text-xs font-bold rounded-full animate-pulse">
                                  HOJE!
                                </span>
                              )}
                              {!isToday && daysUntil <= 3 && (
                                <span className="ml-2 px-2 py-1 bg-purple-500 text-white text-xs font-bold rounded-full">
                                  Em {daysUntil} dia{daysUntil > 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {day.users.map((user, userIndex) => (
                              <div
                                key={user.id}
                                className={`flex items-center gap-3 p-3 rounded-lg ${
                                  isToday 
                                    ? 'bg-gradient-to-r from-pink-100 to-purple-100 border-2 border-pink-300' 
                                    : 'bg-white border border-gray-200'
                                } transform transition-all duration-300 hover:scale-105 hover:shadow-md`}
                              >
                                <div className="relative">
                                  <div className={`h-12 w-12 rounded-full flex items-center justify-center text-white font-bold text-lg ${
                                    isToday 
                                      ? 'bg-gradient-to-br from-pink-500 to-purple-500 shadow-lg' 
                                      : 'bg-gradient-to-br from-pink-400 to-purple-400'
                                  }`}>
                                    {user.full_name.charAt(0).toUpperCase()}
                                  </div>
                                  {isToday && (
                                    <div className="absolute -top-1 -right-1">
                                      <Gift className="h-5 w-5 text-yellow-400 animate-spin-slow" />
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-gray-900 truncate">
                                    {user.full_name}
                                  </p>
                                  <p className="text-sm text-gray-600">
                                    {isToday ? (
                                      <span className="text-pink-600 font-bold">
                                        🎂 {user.age || 'Aniversariando'} anos hoje!
                                      </span>
                                    ) : (
                                      <span>
                                        {user.age || 'Aniversariando'} anos
                                      </span>
                                    )}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {user.role}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          <style>{`
            @keyframes spin-slow {
              from {
                transform: rotate(0deg);
              }
              to {
                transform: rotate(360deg);
              }
            }
            .animate-spin-slow {
              animation: spin-slow 3s linear infinite;
            }
          `}</style>
        </div>
      )}
    </>
  );
};

export default BirthdayIcon;
